import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import { type TranscriptArchiveProgress, useTranscriptArchive } from '@/entities/search';
import { readApiErrorMessage } from '@/shared/api';
import { toastError, toastSuccess } from '@/shared/lib';

/** Параметры скачивания архива транскрипций из блока массовых действий */
type Params = {
  /** Отмеченные диалоги — из них и собирается архив */
  conversationIds: number[];
  /**
   * Архив не запускается из-за другого действия: набирается «выбрать всё» (состав неполон) или идёт
   * удаление (отмеченные диалоги уходят)
   */
  isLocked: boolean;
};

/** Состояние и обработчик скачивания архива транскрипций */
type Result = {
  /** Архив формируется: от запуска до скачивания или отказа */
  isTranscriptArchivePending: boolean;
  /** Ход формирования; `null`, пока формирование не запущено */
  transcriptArchiveProgress: TranscriptArchiveProgress | null;
  /** Запуск архива по отмеченным диалогам с выбранными шаблоном и форматом */
  onDownloadTranscriptArchive: (selection: DownloadTemplateSelection) => void;
};

/**
 * Скачивание транскрипций отмеченных диалогов одним архивом: запуск, итоговое уведомление и ход
 * формирования для блока действий. Отметки строк запуск не снимает.
 */
export const useMassTranscriptArchive = ({ conversationIds, isLocked }: Params): Result => {
  const { t } = useTranslation(['dialogs']);
  const { start, isPending, progress } = useTranscriptArchive();

  const handleDownloadTranscriptArchive = useCallback(
    (selection: DownloadTemplateSelection) => {
      if (conversationIds.length === 0 || isPending || isLocked) return;
      start(
        { filters: { conversationIds }, ...selection },
        {
          onSuccess: (result) => {
            if (result.status === 'downloaded') {
              toastSuccess(
                result.failed > 0
                  ? t('dialogs:dialogListSection.transcriptArchive.successWithFailed', { count: result.failed })
                  : t('dialogs:dialogListSection.transcriptArchive.success'),
              );
            } else if (result.status === 'failed') {
              toastError(t('dialogs:dialogListSection.transcriptArchive.error'), {
                description: result.error ?? undefined,
              });
            }
          },
          onError: async (error) => {
            toastError(t('dialogs:dialogListSection.transcriptArchive.error'), {
              description: await readApiErrorMessage(error),
            });
          },
        },
      );
    },
    [conversationIds, isPending, isLocked, start, t],
  );

  return {
    isTranscriptArchivePending: isPending,
    transcriptArchiveProgress: progress,
    onDownloadTranscriptArchive: handleDownloadTranscriptArchive,
  };
};
